import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGrowthMetrics } from "@/hooks/useGrowthMetrics";
import { useGrowthMetricDialog } from "@/contexts/GrowthMetricDialogContext";

const GlobalGrowthMetricDialog = () => {
  const { isOpen, growthPillarId, brandId, onSuccessCallback, closeMetricDialog } = useGrowthMetricDialog();
  const { createGrowthMetric, isLoading } = useGrowthMetrics(growthPillarId || undefined);
  
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<'North Star' | 'Supportive Metric'>('Supportive Metric');

  const resetForm = () => {
    setName("");
    setDescription("");
    setCategory('Supportive Metric');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim() || !growthPillarId || !brandId) return;

    const success = await createGrowthMetric({
      name: name.trim(),
      description: description.trim(),
      growth_pillar_id: growthPillarId,
      brand_id: brandId,
      category,
    });

    if (success) {
      resetForm();
      onSuccessCallback?.();
      closeMetricDialog();
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetForm();
      closeMetricDialog();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange} modal={true}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New Growth Metric</DialogTitle>
          <DialogDescription>
            Add a new metric to track growth performance. North Star metrics are your primary success indicators.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Monthly Active Users"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this metric measures..."
                rows={3}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="category">Category *</Label>
              <Select value={category} onValueChange={(value: 'North Star' | 'Supportive Metric') => setCategory(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="North Star">North Star</SelectItem>
                  <SelectItem value="Supportive Metric">Supportive Metric</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !name.trim()}>
              {isLoading ? "Creating..." : "Create Metric"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export { GlobalGrowthMetricDialog };
